import React, { FormEvent, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { authApi } from "../api/auth.api";
import { AuthApiError } from "../model/auth.types";
import { ROUTES } from "../../../system/router/routes";
import {
  getPasswordRequirements,
  isPasswordPolicyValid,
  passwordsMatch,
} from "../model/password-policy";
import {
  AuthAlert,
  AuthCard,
  AuthPasswordField,
  AuthSubmitButton,
} from "./auth-kit";
import { AuthLayout } from "./auth-layout";
import * as Styled from "./auth.styled";

export const CreatePasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const requirements = useMemo(
    () => getPasswordRequirements(password),
    [password],
  );

  const createPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setApiError("");
    setAttemptedSubmit(true);

    if (!token) {
      setApiError("Missing setup token. Use the latest password setup link.");
      return;
    }

    if (!requirements.every((requirement) => requirement.valid)) {
      setApiError("Not all password requirements are met.");
      return;
    }

    if (!passwordsMatch(password, confirmPassword)) {
      setApiError("Password doesn't match. Please check and try again.");
      return;
    }

    if (!isPasswordPolicyValid(password, confirmPassword)) {
      setApiError("Not all password requirements are met.");
      return;
    }

    setLoading(true);

    try {
      await authApi.createPassword({ token, password });
      navigate(ROUTES.SIGN_IN, {
        replace: true,
        state: { passwordResetSuccess: true },
      });
    } catch (error) {
      const authError = error as AuthApiError;
      setApiError(authError.message || "Unable to save password.");
    } finally {
      setLoading(false);
    }
  };

  const alert = apiError ? (
    <AuthAlert onClose={() => setApiError("")}>{apiError}</AuthAlert>
  ) : undefined;

  return (
    <AuthLayout alert={alert}>
      <AuthCard title="Create new password">
        <Styled.Form onSubmit={createPassword} noValidate>
          <AuthPasswordField
            id="new-password"
            label="New Password"
            name="newPassword"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
          />

          <AuthPasswordField
            id="confirm-password"
            label="Confirm New Password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            autoComplete="new-password"
          />

          <Styled.RequirementList aria-label="Password requirements">
            {requirements.map((requirement) => (
              <Styled.RequirementItem
                key={requirement.id}
                $valid={requirement.valid}
                $invalid={attemptedSubmit && !requirement.valid}
              >
                {requirement.label}
              </Styled.RequirementItem>
            ))}
          </Styled.RequirementList>

          {!token ? (
            <Styled.FieldError>
              Missing setup token. Use the latest password setup link.
            </Styled.FieldError>
          ) : null}

          <AuthSubmitButton
            loading={loading}
            loadingLabel="Saving..."
            disabled={loading}
          >
            Save
          </AuthSubmitButton>
        </Styled.Form>
      </AuthCard>
    </AuthLayout>
  );
};
